import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";
import { authApi, tenantApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { useToast } from "@/context/ToastContext";

interface RoleItem {
    id?: number | string;
    name?: string;
    tag?: string;
    tenant_id?: number | string;
    tenant?: { id?: number | string; name?: string };
}

export const SettingsAccountTab: React.FC = () => {
    const { t, i18n } = useTranslation("global");
    const { user, roles, logout, setUser } = useAuth();
    const { showError, showSuccess } = useToast();
    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
    const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string>("");
    const [avatarZoom, setAvatarZoom] = useState(1);
    const [avatarX, setAvatarX] = useState(50);
    const [avatarY, setAvatarY] = useState(50);
    const [isAvatarOpen, setIsAvatarOpen] = useState(false);
    const [isAvatarSaving, setIsAvatarSaving] = useState(false);
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const avatarCropRef = useRef<HTMLDivElement>(null);
    const avatarDragRef = useRef<{
        clientX: number;
        clientY: number;
        x: number;
        y: number;
    } | null>(null);

    useEffect(() => {
        return () => {
            if (avatarPreview) {
                URL.revokeObjectURL(avatarPreview);
            }
        };
    }, [avatarPreview]);

    const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !file.type.startsWith("image/")) {
            return;
        }

        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
        setAvatarZoom(1);
        setAvatarX(50);
        setAvatarY(50);
        setIsAvatarOpen(true);
        event.target.value = "";
    };

    const handleAvatarSave = async (): Promise<void> => {
        if (!avatarFile) {
            return;
        }

        setIsAvatarSaving(true);
        try {
            const image = new Image();
            image.src = avatarPreview;
            await image.decode();

            const size = 512;
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const context = canvas.getContext("2d");
            if (!context) {
                return;
            }

            const scale = Math.max(size / image.width, size / image.height) * avatarZoom;
            const width = image.width * scale;
            const height = image.height * scale;
            const left = (size - width) * (avatarX / 100);
            const top = (size - height) * (avatarY / 100);
            context.drawImage(image, left, top, width, height);

            const blob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob(resolve, "image/webp", 0.9)
            );
            if (!blob) {
                return;
            }

            const response = await authApi.uploadAvatar(
                new File([blob], "avatar.webp", { type: "image/webp" })
            );
            if (!response.result || !response.data?.user) {
                throw new Error(t("account_avatar.error"));
            }

            setUser(response.data.user);
            localStorage.setItem("user", JSON.stringify(response.data.user));
            setIsAvatarOpen(false);
            setAvatarFile(null);
            showSuccess(t("account_avatar.updated"));
        } catch (error) {
            showError(
                error instanceof Error
                    ? error.message
                    : t("account_avatar.error")
            );
        } finally {
            setIsAvatarSaving(false);
        }
    };

    const startAvatarDrag = (
        event: React.PointerEvent<HTMLDivElement>
    ): void => {
        avatarDragRef.current = {
            clientX: event.clientX,
            clientY: event.clientY,
            x: avatarX,
            y: avatarY,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const moveAvatar = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!avatarDragRef.current || !avatarCropRef.current) {
            return;
        }

        const rect = avatarCropRef.current.getBoundingClientRect();
        const drag = avatarDragRef.current;
        const deltaX = ((event.clientX - drag.clientX) / rect.width) * 100;
        const deltaY = ((event.clientY - drag.clientY) / rect.height) * 100;

        setAvatarX(Math.max(0, Math.min(100, drag.x + deltaX)));
        setAvatarY(Math.max(0, Math.min(100, drag.y + deltaY)));
    };

    useEffect(() => {
        const activeCompany = localStorage.getItem("vigepra_active_company");

        if (activeCompany && activeCompany !== "main") {
            return;
        }

        void tenantApi.getAll().then((response) => {
            const companies = response?.data?.companies || response?.data || [];
            const names = companies.reduce(
                (result: Record<string, string>, company: { id: number; name?: string }) => {
                    if (company.name) {
                        result[String(company.id)] = company.name;
                    }
                    return result;
                },
                {}
            );
            setTenantNames(names);
        }).catch(() => undefined);
    }, []);

    const getInitials = (name?: string): string => {
        if (!name) {
            return "VT";
        }
        const parts = name.trim().split(" ");
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const initials = getInitials(user?.name);

    const roleItems: RoleItem[] = (() => {
        if (Array.isArray(user?.roles) && user.roles.length > 0) {
            return user.roles
                .map((r: RoleItem | string) => {
                    if (typeof r === "string") {
                        return { name: r, tag: r };
                    }

                    return r;
                })
                .filter((r) => Boolean(r.name || r.tag));
        }
        if (Array.isArray(roles) && roles.length > 0) {
            return roles.map((role) => ({ name: role, tag: role }));
        }
        if (user?.role) {
            return [{ name: String(user.role), tag: String(user.role) }];
        }
        return [];
    })();

    const activeCompanyId = localStorage.getItem("vigepra_active_company");
    const isMainCompany = !activeCompanyId || activeCompanyId === "main";
    const companies = Array.isArray(user?.companies) ? user.companies : [];
    const activeCompanyName = companies.find(
        (company) => String(company.id) === String(activeCompanyId)
    )?.name;

    const getRoleCompanyName = (role: RoleItem): string | undefined => {
        if (role.tenant?.name) {
            return role.tenant.name;
        }

        return companies.find(
            (company) =>
                String(company.id) === String(role.tenant_id)
        )?.name ||
            tenantNames[String(role.tenant_id)] ||
            undefined;
    };

    const visibleRoleItems = roleItems.filter((role) => {
        if (isMainCompany || !activeCompanyId || !role.tenant_id) {
            return true;
        }

        return String(role.tenant_id) === String(activeCompanyId);
    });

    const getRoleLabel = (tag: string): string => {
        const tagLower = tag.toLowerCase();
        const tagKey = `roles.tags.${tagLower}`;
        return t(tagKey);
    };

    const handleLogout = async (): Promise<void> => {
        setIsLoggingOut(true);
        try {
            await logout();
            navigate("/login");
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in max-w-2xl mx-auto w-full">
            {/* Profile Card */}
            <div className="box flex flex-col items-center justify-center text-center p-6 sm:p-8">
                {/* Round Avatar */}
                <button
                    type="button"
                    className="group relative w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-primary text-white font-black text-2xl sm:text-3xl flex items-center justify-center select-none shadow-md border-2 border-primary mb-3.5 cursor-pointer"
                    onClick={() => avatarInputRef.current?.click()}
                    title={t("account_avatar.change")}
                >
                    {user?.avatar ? (
                        <img src={user.avatar} alt="" className="h-full w-full rounded-full object-cover" />
                    ) : (
                        initials
                    )}
                    <div className="absolute right-0 top-0 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                        <Icon value="pencil" size={13} />
                    </div>
                </button>
                <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleAvatarSelect}
                />

                {/* Name */}
                <h3 className="box-title sm:text-lg truncate">
                    {user?.name || "Usuario"}
                </h3>

                {/* Email */}
                <div className="box-subtitle sm:text-sm truncate">
                    {user?.email || ""}
                </div>

                {/* Dynamic Role Badges with Tag Translations */}
                {visibleRoleItems.length > 0 && (
                    <div className="w-full mt-3.5">
                        {isMainCompany && (
                            <h4 className="mb-2 text-left text-sm font-bold text-slate-700 dark:text-zinc-200">
                                {t("companies.title")}
                            </h4>
                        )}
                        <div
                            className={
                                isMainCompany
                                    ? "flex flex-col items-center gap-2"
                                    : "flex flex-wrap items-center justify-center gap-2"
                            }
                        >
                        {isMainCompany
                            ? Object.entries(
                                  visibleRoleItems.reduce<
                                      Record<string, RoleItem[]>
                                  >((groups, role) => {
                                      const company =
                                          getRoleCompanyName(role) ||
                                          t("companies.title");
                                      groups[company] = groups[company] || [];
                                      groups[company].push(role);
                                      return groups;
                                  }, {})
                              )
                                  .sort(([companyA], [companyB]) =>
                                      companyA.localeCompare(companyB, undefined, {
                                          sensitivity: "base",
                                      })
                                  )
                                  .map(([company, companyRoles]) => (
                                  <div
                                      key={company}
                                      className="w-full rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-center dark:border-white/10 dark:bg-white/5"
                                  >
                                      <div className="mb-2 text-sm font-bold text-primary">
                                          {company}
                                      </div>
                                      <div className="flex flex-wrap items-center justify-center gap-2">
                                          {companyRoles.map((role, index) => (
                                              <Badge
                                                  key={`${company}-${role.id || role.tag}-${index}`}
                                                  variant="primary"
                                              >
                                                  {getRoleLabel(
                                                      role.tag || role.name || ""
                                                  )}
                                              </Badge>
                                          ))}
                                      </div>
                                  </div>
                              ))
                              : [
                                    [
                                        activeCompanyName ||
                                            getRoleCompanyName(
                                                visibleRoleItems[0]
                                            ) ||
                                            t("companies.title"),
                                        visibleRoleItems,
                                    ] as [string, RoleItem[]],
                                ].map(([company, companyRoles]) => (
                                    <div
                                        key={company}
                                        className="w-full rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-center dark:border-white/10 dark:bg-white/5"
                                    >
                                        <div className="mb-2 text-sm font-bold text-primary">
                                            {company}
                                        </div>
                                        <div className="flex flex-wrap items-center justify-center gap-2">
                                            {companyRoles.map((role, index) => (
                                                <Badge
                                                    key={`${company}-${role.id || role.tag}-${index}`}
                                                    variant="primary"
                                                >
                                                    {getRoleLabel(
                                                        role.tag ||
                                                            role.name ||
                                                            ""
                                                    )}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </div>

            <Modal
                isOpen={isAvatarOpen}
                onClose={() => setIsAvatarOpen(false)}
                title={t("account_avatar.title")}
                maxWidth="sm"
                onSubmit={handleAvatarSave}
                submitLabel={t("account_avatar.save")}
                isSubmitting={isAvatarSaving}
            >
                <div className="space-y-4">
                     <div className="text-center text-xs text-slate-500 dark:text-zinc-400">
                        {t("account_avatar.drag_hint")}
                    </div>
                    <div
                        ref={avatarCropRef}
                        className="relative mx-auto h-64 w-64 touch-none cursor-move rounded-full bg-slate-100"
                        style={{
                            overflow: "hidden",
                            borderRadius: "50%",
                            clipPath: "circle(50%)",
                        }}
                        onPointerMove={moveAvatar}
                        onPointerDown={startAvatarDrag}
                        onPointerUp={() => {
                            avatarDragRef.current = null;
                        }}
                        onPointerCancel={() => {
                            avatarDragRef.current = null;
                        }}
                    >
                        {avatarPreview && (
                            <div
                                aria-label="Vista previa"
                                className="absolute inset-0 rounded-full bg-cover bg-no-repeat"
                                style={{
                                    backgroundImage: `url("${avatarPreview}")`,
                                    backgroundPosition: `${avatarX}% ${avatarY}%`,
                                    backgroundSize: `${avatarZoom * 100}%`,
                                }}
                            />
                        )}
                    </div>
                    <label className="block text-sm font-semibold">
                        <div>{t("account_avatar.zoom")}</div>
                        <input
                            type="range"
                            min="1"
                            max="3"
                            step="0.05"
                            value={avatarZoom}
                            onChange={(event) => setAvatarZoom(Number(event.target.value))}
                            className="mt-2 w-full accent-primary"
                        />
                    </label>
                </div>
            </Modal>

            {/* Centered Logout Button */}
            <div className="flex justify-center pt-2">
                <Button
                    variant="danger"
                    icon={<Icon value="power" size={16} />}
                    onClick={handleLogout}
                    loading={isLoggingOut}
                >
                    {t("auth.logout")}
                </Button>
            </div>
        </div>
    );
};
